import { Copy } from "lucide-react";
import gcAd1 from "@/assets/gc-ad-1-diagnosis.jpg";
import gcAd2 from "@/assets/gc-ad-2-wasted-spend.jpg";
import gcAd3 from "@/assets/gc-ad-3-serious-filter.jpg";
import gcAd4 from "@/assets/gc-ad-4-systems.jpg";
import gcAd5 from "@/assets/gc-ad-5-direct.jpg";
import gcAd6 from "@/assets/gc-ad-6-retarget-clarity.jpg";
import gcAd7 from "@/assets/gc-ad-7-retarget-finish.jpg";
import gcAd8 from "@/assets/gc-ad-8-retarget-bottleneck.jpg";

const angleMap = [
  { id: "#1", angle: "Diagnosis", emotion: "Clarity over confusion", priority: "Batch 1", type: "Prospecting" },
  { id: "#2", angle: "Wasted Spend", emotion: "Frustration / waste", priority: "Batch 1", type: "Prospecting" },
  { id: "#3", angle: "Serious Practice Filter", emotion: "Identity / pride", priority: "Batch 1", type: "Prospecting" },
  { id: "#4", angle: "Systems Thinking", emotion: "Strategic control", priority: "Batch 1", type: "Prospecting" },
  { id: "#5", angle: "Direct / Short Copy", emotion: "Action-oriented", priority: "Batch 2", type: "Prospecting" },
  { id: "#6", angle: "Retargeting — Clarity", emotion: "Unfinished action", priority: "Batch 1", type: "Retargeting" },
  { id: "#7", angle: "Retargeting — Finish", emotion: "Warm intent", priority: "Batch 1", type: "Retargeting" },
  { id: "#8", angle: "Retargeting — Bottleneck", emotion: "Diagnostic recall", priority: "Batch 2", type: "Retargeting" },
];

const ads = [
  {
    id: 1,
    angle: "Diagnosis Angle — Funnel Bottleneck Review",
    recommended: true,
    startHere: true,
    image: gcAd1,
    primary: `Most dental practices do not need more random marketing tactics. They need to know where growth is breaking down.\n\nSometimes the issue is visibility. Sometimes it is the website. Sometimes it is the handoff between calls, forms, and new-patient scheduling.\n\nXpress Growth Checkup is built to help established dental practices see what is slowing growth before spending more.`,
    headline: "Dentists: Find What Is Slowing Your Growth",
    description: "A practical growth checkup for established dental practices.",
    button: "Learn More",
  },
  {
    id: 2,
    angle: "Wasted Spend Angle — Before You Spend More",
    recommended: true,
    startHere: true,
    image: gcAd2,
    primary: `Dentists: If your practice is spending on marketing but new-patient growth still feels inconsistent, the problem may not be more traffic.\n\nIt may be the website, Google presence, or new-patient path that turns attention into real appointments.\n\nThis checkup is designed to show dental practice owners where the bottleneck is first, so the next decision is clearer.`,
    headline: "Dentists: Before You Increase Spend, Check The System",
    description: "Review the website, visibility, and new-patient path for your dental practice.",
    button: "Learn More",
  },
  {
    id: 3,
    angle: "Serious Practice Filter — Built for Established Practices",
    recommended: true,
    image: gcAd3,
    primary: `This is built for dental practice owners who take growth seriously.\n\nIf you want a clearer read on what is helping or hurting your new-patient flow, Xpress will review the key moving parts and point to the priorities most worth fixing first.\n\nThis is not for dentists looking for a magic bullet. It is for dental practice owners who want better decisions.`,
    headline: "Dental Practice Owners: A Growth Checkup Built For You",
    description: "Built for dentists who value clarity, systems, and follow-through.",
    button: "Learn More",
  },
  {
    id: 4,
    angle: "Systems Angle — Coordinated Growth",
    recommended: true,
    image: gcAd4,
    primary: `Dental practice growth does not come from isolated tactics. It comes from coordinated systems: visibility, website conversion, trust, and new-patient follow-through.\n\nXpress has worked exclusively with dental practices across websites, direct mail, ads, and growth strategy for years. This checkup helps dentists identify which part of the system deserves attention first.`,
    headline: "Dentists: Growth Comes From Systems, Not Random Tactics",
    description: "See where your dental practice is losing new-patient opportunities.",
    button: "Learn More",
  },
  {
    id: 5,
    angle: "Direct Angle — Short Form Static",
    recommended: false,
    image: gcAd5,
    primary: `Dentists: Not seeing the new-patient growth your marketing should be producing?\n\nBefore adding more tactics, find out where the current system is leaking.\n\nRequest the Xpress Growth Checkup — built exclusively for dental practices.`,
    headline: "Dentists: Request Your Growth Checkup",
    description: "Built exclusively for established dental practices.",
    button: "Learn More",
  },
  {
    id: 6,
    angle: "Retargeting — Return to Clarity",
    retargeting: true,
    image: gcAd6,
    primary: `Dentists: Still weighing whether your practice should request the Growth Checkup?\n\nIf new-patient growth feels uneven, the most useful next step may be clarity on where the breakdown is happening.\n\nReview the website, visibility, and new-patient path before making your next marketing decision.`,
    headline: "Dentists: See What Is Slowing Your Growth",
    description: "Get clearer before you spend more on your dental practice.",
    button: "Learn More",
  },
  {
    id: 7,
    angle: "Retargeting — Finish The Request",
    retargeting: true,
    image: gcAd7,
    primary: `Dentists: If you visited the Xpress checkup page but did not finish, you can still request a review.\n\nThis is designed for established dental practice owners who want a clearer read on what deserves attention first.`,
    headline: "Dentists: Finish Your Growth Checkup Request",
    description: "Built for serious dental practices.",
    button: "Learn More",
  },
  {
    id: 8,
    angle: "Retargeting — Find The Bottleneck",
    retargeting: true,
    image: gcAd8,
    primary: `Dentists: More marketing activity does not automatically create better new-patient growth.\n\nSometimes the next win comes from identifying the bottleneck first.`,
    headline: "Dentists: Find The Bottleneck First",
    description: "Review what deserves attention first in your dental practice.",
    button: "Learn More",
  },
];

const copyToClipboard = (text: string) => {
  navigator.clipboard.writeText(text);
};

const priorityColor = (p: string) => {
  if (p === "Batch 1") return "bg-emerald-500/20 text-emerald-400";
  return "bg-amber-500/20 text-amber-400";
};

const typeColor = (t: string) => {
  return t === "Retargeting" ? "bg-purple-500/20 text-purple-400" : "bg-sky-500/20 text-sky-400";
};

const GcAdsPage = () => {
  return (
    <div className="min-h-screen bg-hero-bg text-hero-foreground">
      {/* Header */}
      <div className="py-12 md:py-16">
        <div className="max-w-5xl mx-auto px-6">
          <p className="text-accent font-semibold text-sm tracking-wide uppercase mb-2">
            Facebook Ad Creatives — Growth Checkup Campaign
          </p>
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight mb-3">
            Xpress Growth Checkup — 8 Ad Concepts
          </h1>
          <p className="text-hero-foreground/60 max-w-2xl leading-relaxed">
            8 ad concepts (5 prospecting + 3 retargeting) targeting dental practice owners who want clarity on what is slowing growth. Each ad drives to the{" "}
            <a href="/GetGrowthScore" className="text-accent hover:underline">
              Growth Checkup landing page
            </a>
            . Start with the first batch of 4 prospecting + 2 retargeting for the best initial test.
          </p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 pb-16">
        {/* Setup Notes */}
        <div className="bg-hero-foreground/5 rounded-xl border border-hero-foreground/10 p-6 mb-10">
          <h2 className="font-bold mb-3">FB Ads Manager Setup</h2>
          <div className="grid sm:grid-cols-2 gap-4 text-sm text-hero-foreground/60">
            <div>
              <p className="font-medium text-hero-foreground mb-1">Campaign Objective</p>
              <p>Leads or Sales → Website conversions (Landing Page Views initially, then shift to form submit)</p>
            </div>
            <div>
              <p className="font-medium text-hero-foreground mb-1">Targeting</p>
              <p>Dentists, dental practice owners, cosmetic/family dentist, 30–64 age, decision-makers</p>
            </div>
            <div>
              <p className="font-medium text-hero-foreground mb-1">Destination URL</p>
              <p className="font-mono text-xs break-all">
                {window.location.origin}/GetGrowthScore?utm_source=facebook&utm_medium=paid&utm_campaign=growth-checkup
              </p>
            </div>
            <div>
              <p className="font-medium text-hero-foreground mb-1">Placements</p>
              <p>Facebook Feed, Instagram Feed, Facebook Stories (start manual, then expand)</p>
            </div>
          </div>
        </div>

        {/* Creative System Overview */}
        <div className="bg-hero-foreground/5 rounded-xl border border-hero-foreground/10 p-6 mb-10">
          <h2 className="font-bold mb-4">Creative System Overview</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
            <div>
              <p className="font-medium text-hero-foreground mb-1">Brand Feel</p>
              <p className="text-hero-foreground/60">Direct, credible, diagnostic, calm, authority-first</p>
            </div>
            <div>
              <p className="font-medium text-hero-foreground mb-1">Color System</p>
              <p className="text-hero-foreground/60">Xpress red, charcoal, white, slate gray</p>
            </div>
            <div>
              <p className="font-medium text-hero-foreground mb-1">Type Treatment</p>
              <p className="text-hero-foreground/60">Bold condensed headline, clean sans supporting copy</p>
            </div>
            <div>
              <p className="font-medium text-hero-foreground mb-1">Image Style</p>
              <p className="text-hero-foreground/60">Editorial report, dashboard, screen analysis, structured overlays</p>
            </div>
            <div>
              <p className="font-medium text-hero-foreground mb-1">Avoid</p>
              <p className="text-hero-foreground/60">Generic smiling patients, cheesy stock, flashy promise graphics</p>
            </div>
            <div>
              <p className="font-medium text-hero-foreground mb-1">CTA Tone</p>
              <p className="text-hero-foreground/60">Learn more, request checkup, find the bottleneck</p>
            </div>
          </div>
        </div>

        {/* Angle Map Table */}
        <div className="bg-hero-foreground/5 rounded-xl border border-hero-foreground/10 p-6 mb-10">
          <h2 className="font-bold mb-4">Angle Map — What Each Ad Targets</h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-hero-foreground/50 text-xs uppercase tracking-wider">
                  <th className="text-left pb-3 pr-4">Ad</th>
                  <th className="text-left pb-3 pr-4">Angle</th>
                  <th className="text-left pb-3 pr-4">Core Emotion</th>
                  <th className="text-left pb-3 pr-4">Type</th>
                  <th className="text-left pb-3">Priority</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-hero-foreground/10">
                {angleMap.map((row) => (
                  <tr key={row.id}>
                    <td className="py-3 pr-4 font-bold">{row.id}</td>
                    <td className="py-3 pr-4">{row.angle}</td>
                    <td className="py-3 pr-4 text-hero-foreground/70">{row.emotion}</td>
                    <td className="py-3 pr-4">
                      <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${typeColor(row.type)}`}>
                        {row.type}
                      </span>
                    </td>
                    <td className="py-3">
                      <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${priorityColor(row.priority)}`}>
                        {row.priority}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Ad Cards */}
        <div className="space-y-12">
          {ads.map((ad) => (
            <div
              key={ad.id}
              className="bg-hero-foreground/5 rounded-xl border border-hero-foreground/10 overflow-hidden"
            >
              {/* Ad Header */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-hero-foreground/10">
                <div className="flex items-center gap-3">
                  <span className="w-8 h-8 rounded-full bg-accent text-accent-foreground flex items-center justify-center text-sm font-bold">
                    {ad.id}
                  </span>
                  <h3 className="font-bold">{ad.angle}</h3>
                </div>
                <div className="flex items-center gap-2">
                  {"retargeting" in ad && ad.retargeting && (
                    <span className="bg-purple-500/15 text-purple-400 text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wide">
                      Retargeting
                    </span>
                  )}
                  {"recommended" in ad && ad.recommended && (
                    <span className="bg-amber-500/15 text-amber-400 text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wide">
                      Recommended
                    </span>
                  )}
                  {"startHere" in ad && ad.startHere && (
                    <span className="bg-emerald-500/15 text-emerald-400 text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wide">
                      Start Here
                    </span>
                  )}
                </div>
              </div>

              <div className="grid md:grid-cols-2">
                {/* Creative Preview */}
                <div className="p-6 flex items-center justify-center">
                  <div className="w-full max-w-sm">
                    <div className="bg-hero-foreground/10 rounded-lg border border-hero-foreground/10 overflow-hidden">
                      <div className="p-3 flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full bg-accent flex items-center justify-center text-accent-foreground text-xs font-bold">
                          XP
                        </div>
                        <div>
                          <p className="text-xs font-semibold">Xpress Promotion</p>
                          <p className="text-[10px] text-hero-foreground/50">Sponsored · 🌐</p>
                        </div>
                      </div>
                      <p className="px-3 pb-2 text-xs text-hero-foreground/80 line-clamp-3">
                        {ad.primary.split("\n")[0]}
                      </p>
                      <img
                        src={ad.image}
                        alt={ad.angle}
                        className="w-full aspect-square object-cover"
                        loading="lazy"
                        width={1080}
                        height={1080}
                      />
                      <div className="p-3 border-t border-hero-foreground/10">
                        <p className="text-[10px] text-hero-foreground/50 uppercase">
                          {window.location.host}
                        </p>
                        <p className="text-xs font-semibold truncate">{ad.headline}</p>
                        <p className="text-[10px] text-hero-foreground/50 truncate">{ad.description}</p>
                      </div>
                      <div className="px-3 pb-3">
                        <div className="bg-hero-foreground/10 text-hero-foreground text-xs font-semibold text-center py-2 rounded">
                          {ad.button}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Copy */}
                <div className="p-6 space-y-5">
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="text-xs font-bold uppercase tracking-wide text-hero-foreground/50">
                        Primary Text
                      </label>
                      <button
                        onClick={() => copyToClipboard(ad.primary)}
                        className="text-xs text-accent hover:underline flex items-center gap-1"
                      >
                        <Copy className="w-3 h-3" /> Copy
                      </button>
                    </div>
                    <div className="bg-hero-foreground/5 rounded-lg p-4 text-sm whitespace-pre-line leading-relaxed max-h-60 overflow-y-auto border border-hero-foreground/10">
                      {ad.primary}
                    </div>
                  </div>

                  <div>
                    <label className="text-xs font-bold uppercase tracking-wide text-hero-foreground/50 block mb-1">
                      Headline
                    </label>
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-semibold flex-1">{ad.headline}</p>
                      <button
                        onClick={() => copyToClipboard(ad.headline)}
                        className="text-hero-foreground/40 hover:text-accent"
                      >
                        <Copy className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>

                  <div>
                    <label className="text-xs font-bold uppercase tracking-wide text-hero-foreground/50 block mb-1">
                      Description
                    </label>
                    <div className="flex items-center gap-2">
                      <p className="text-sm text-hero-foreground/70 flex-1">{ad.description}</p>
                      <button
                        onClick={() => copyToClipboard(ad.description)}
                        className="text-hero-foreground/40 hover:text-accent"
                      >
                        <Copy className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>

                  <div>
                    <label className="text-xs font-bold uppercase tracking-wide text-hero-foreground/50 block mb-1">
                      CTA Button
                    </label>
                    <p className="text-sm font-medium">{ad.button}</p>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Best First Batch */}
        <div className="mt-12 bg-hero-foreground/5 rounded-xl border border-hero-foreground/10 p-6">
          <h3 className="font-bold mb-5">Best First Creative Batch</h3>
          <p className="text-sm text-hero-foreground/60 mb-4">Build 4 prospecting + 2 retargeting visuals first for message contrast without too much production overhead.</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {[
              { label: "#1 Diagnosis Angle", type: "Prospecting" },
              { label: "#2 Wasted Spend Angle", type: "Prospecting" },
              { label: "#3 Serious Practice Filter", type: "Prospecting" },
              { label: "#4 Systems Angle", type: "Prospecting" },
              { label: "#6 Retargeting — Clarity", type: "Retargeting" },
              { label: "#7 Retargeting — Finish", type: "Retargeting" },
            ].map((item) => (
              <div key={item.label} className="flex items-center gap-2 bg-hero-foreground/5 rounded-lg px-4 py-3 border border-hero-foreground/10">
                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${typeColor(item.type)}`}>
                  {item.type}
                </span>
                <span className="text-sm font-medium">{item.label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Production Notes */}
        <div className="mt-8 bg-hero-foreground/5 rounded-xl border border-hero-foreground/10 p-6">
          <h3 className="font-bold mb-4">Production Notes</h3>
          <ul className="space-y-2 text-sm text-hero-foreground/60">
            <li>• Each image should feel like it came from the same campaign family</li>
            <li>• Use the Xpress logo sparingly but consistently</li>
            <li>• Keep the layout clean, with bold hierarchy and generous spacing</li>
            <li>• The headline should do most of the work — the graphic reinforces the idea</li>
            <li>• Best-performing creative will feel more like a diagnostic business asset than a traditional dental ad</li>
            <li>• Use one reusable visual system — swap headline, subline, and diagram for each concept</li>
          </ul>
        </div>

        {/* What To Avoid */}
        <div className="mt-8 bg-hero-foreground/5 rounded-xl border border-hero-foreground/10 p-6">
          <h3 className="font-bold mb-4">What To Avoid</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-hero-foreground/50 text-xs uppercase tracking-wider">
                  <th className="text-left pb-3 pr-4">❌ Avoid</th>
                  <th className="text-left pb-3">✅ Use Instead</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-hero-foreground/10">
                {[
                  ["Generic smiling patients", "Editorial report / dashboard visuals"],
                  ["Cheesy stock poses", "Team-in-review or screen analysis shots"],
                  ["Flashy promise graphics", "Structured overlays with diagnostic feel"],
                  ["Cluttered layouts", "Clean hierarchy with generous spacing"],
                  ['Broad "grow your practice" language', "Website, visibility, intake messaging"],
                ].map(([avoid, use], i) => (
                  <tr key={i}>
                    <td className="py-3 pr-4 text-red-400">{avoid}</td>
                    <td className="py-3 text-emerald-400">{use}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GcAdsPage;
